<template>
  <layout-report-step
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :image="header.image"
    :debug="header.debug"
    :showFooter="false"
    :hasInstructions="true"
    :instructionPictures="instruction.pictures"
    :instructionText="instruction.text"
    :instructionTitle="instruction.title"
    @next="next"
  >
    <v-sheet v-if="isLoading" rounded outlined class="mb-2 py-n6 mt-2">
      <v-skeleton-loader type="article" />
    </v-sheet>

    <div v-else>
      <div v-if="activePromotion && activePromotion.isApplicable">
        <v-radio-group v-model="promotion">
          <v-sheet outlined rounded color="primary" class="mb-2 py-n6">
            <v-card flat>
              <thg-promotion-list-item :value="activePromotion" />
            </v-card>
          </v-sheet>
        </v-radio-group>

        <v-list-item class="mt-n2">
          <v-spacer />
          <v-icon x-large>mdi-plus</v-icon>
          <v-spacer />
        </v-list-item>
      </div>
      <v-radio-group v-model="vehicleClass">
        <v-sheet
          outlined
          rounded
          :color="activeCardStyle(products.vehicleType)"
          v-for="(products, i) in availableVehicleClasses"
          :key="i"
          class="mb-2 py-n6"
        >
          <v-card flat @click="vehicleClass = products.vehicleType">
            <v-list three-line>
              <v-list-item>
                <v-list-item-avatar tile size="60">
                  <v-img v-if="products.src" :src="products.src" style="border-radius: 4px" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ $t(products.title) }} </v-list-item-title>
                  <v-list-item-subtitle
                    ><span v-if="products.hint">{{ $t(products.hint) + " " }}</span
                    >{{ $t(products.classSpeaking) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-for="year in selectedYears" :key="year">
                    {{
                      year +
                      ": " +
                      $t(getPrice(year, products.vehicleType).prefix) +
                      " " +
                      getPrice(year, products.vehicleType).totalAmountAsString
                    }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-radio :value="products.vehicleType"></v-radio>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-sheet>
        <v-card flat v-if="vehicleClassPreview && showPreview">
          <v-btn small text color="info" @click="vehicleClassPreview = false">{{
            $t("report.thgVehicleClass.loadAll")
          }}</v-btn>
        </v-card>
        <div v-if="isCharging">
          <v-subheader>{{ $t("report.thgVehicleClass.charging.header") }}</v-subheader>
          <v-sheet outlined rounded class="mb-2 py-n6">
            <v-card flat @click="goToChargingStationOverview">
              <v-list three-line>
                <v-list-item>
                  <v-list-item-avatar tile size="60">
                    <v-img
                      src="https://static.mmmint.ai/public-thg/2023-09-14-logo-573e2ebe2e509db4908a6f33.jpg"
                      style="border-radius: 4px"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t("report.thgVehicleClass.charging.title") }} </v-list-item-title>
                    <v-list-item-subtitle>{{ $t("report.thgVehicleClass.charging.subtitle") }} </v-list-item-subtitle>
                    <v-list-item-subtitle>{{ $t("report.thgVehicleClass.charging.hint") }} </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-radio @click="goToChargingStationOverview"></v-radio>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-sheet>
        </div>
      </v-radio-group>
    </div>
  </layout-report-step>
</template>

<script lang="ts">
import ThgCheckoutSustainableCard from "@/components/thg/ThgCheckoutSustainableCard.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ThgVehicleTypes } from "@/store/enum/thg/thg-vehicle-types.enum";
import { PartnerActiveOfferModule } from "@/store/modules/partner-active-config.store";
import { ReportModule } from "@/store/modules/report.store";
import { ThgCreateModule } from "@/store/modules/thg.create.store";
import { Component } from "vue-property-decorator";
import { GtagModule } from "@/store/modules/gtag.store";
import ThgPromotionListItem from "@/components/thg/ThgPromotionListItem.vue";

@Component({
  components: {
    LayoutReportStep,
    ThgCheckoutSustainableCard,
    ThgPromotionListItem
  }
})
export default class ThgVehicleClass extends ReportStepMixin implements IReportStep {
  screen = ReportScreenEnum.thgVehicleClass;

  isLoading = false;

  promotion = "";

  get activePromotion() {
    return PartnerActiveOfferModule.currentSignUpPromotion;
  }

  get vehicleClassPreview() {
    return ThgCreateModule.vehicleClassPreview;
  }

  set vehicleClassPreview(isPreview: boolean) {
    ThgCreateModule.setVehicleClassPreview(isPreview);
  }

  get selectedYears() {
    return ThgCreateModule.years;
  }

  getPrice(year: number, vehicleClass: ThgVehicleTypes) {
    return PartnerActiveOfferModule.priceConfig.get({ year: year, vehicleClass: vehicleClass });
  }

  get showPreview() {
    return PartnerActiveOfferModule.priceConfig.vehicleClass.length > 3;
  }

  goToChargingStationOverview() {
    this.$router.push({ name: "ThgChargingStationOverview" });
  }

  get vehicleClass(): ThgVehicleTypes | undefined {
    if (!ThgCreateModule.vehicleClass && this.availableVehicleClasses.length) {
      this.vehicleClass = this.availableVehicleClasses[0].vehicleType;
    }
    return ThgCreateModule.vehicleClass;
  }

  set vehicleClass(vehicleClass: ThgVehicleTypes | undefined) {
    ThgCreateModule.setVehicleClass(vehicleClass);
  }

  public header: IReportHeaderData = {
    title: "report.thgVehicleClass.title",
    description: "report.thgVehicleClass.description"
  };

  public instruction: IReportInstructionData = {
    title: "report.thgVehicleClass.instructionTitle",
    text: "report.thgVehicleClass.instructionText",
    pictures: [AssetEnum.charging],
    example: AssetEnum.charging,
    display: true
  };

  activeCardStyle(productId: ThgVehicleTypes) {
    if (this.vehicleClass === productId) {
      return "primary";
    }
    return "";
  }

  get availableVehicleClasses() {
    const vehicleClasses = PartnerActiveOfferModule.priceConfig.vehicleClass;
    if (this.vehicleClassPreview) {
      return vehicleClasses.slice(0, 3);
    }
    return vehicleClasses;
  }

  get isCharging() {
    return ReportModule.partner.settings?.bookedServices.chargingStation;
  }

  get company() {
    return ReportModule.partner.companyName;
  }

  get items() {
    return ThgCreateModule.yearSelections;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get isDone() {
    return true;
  }

  next() {
    GtagModule.dispatchAddToCart();
  }

  async mounted() {
    this.setCurrentStep();
  }
}
</script>

<style scoped>
.center {
  text-align: center;
}
</style>
